import Icon from '@components/v2/ui/Icon/Icon';
import { Box, ButtonBase, Theme, useTheme } from '@mui/material';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Minus, Plus } from 'lucide-react';
import { useProductQuantityInputStyles } from '../../../../../../../../components/Product/useProductQuantityInputStyles';
import teceoInputConfig from '../../../../../../../../utils/TeceoInputConfig';

interface GetStepIconColorParams {
  isDisabled: boolean;
  hasError: boolean;
}

const getStepIconColor = (theme: Theme, { isDisabled, hasError }: GetStepIconColorParams) => {
  if (hasError) {
    return theme.palette.error.main;
  }

  if (isDisabled) {
    return theme.palette.text.disabled;
  }

  return theme.palette.primary.main;
};

type MobileProductInputProps = {
  disabled: boolean;
  isReadOnly?: boolean;
  textFieldProps: TextFieldProps;
  showStock: boolean;
  inputRef?: React.RefObject<HTMLInputElement> | null;
  stockValue?: number;
};

const MobileProductInput = (props: MobileProductInputProps) => {
  const { disabled, isReadOnly, showStock, textFieldProps, stockValue, inputRef } = props;
  const hasError = !!textFieldProps.error;
  const { cx, classes: productClasses } = useProductQuantityInputStyles({
    hasFocusBorder: !isReadOnly,
    isDisabled: disabled,
    hasError,
  });
  const theme = useTheme();

  const iconColor = getStepIconColor(theme, { isDisabled: disabled, hasError });

  const dispatchEvent = () => {
    const event = new Event('input', { bubbles: true });
    inputRef?.current?.dispatchEvent(event);
  };

  const onClickIncrement = () => {
    if (inputRef?.current?.value === '') {
      inputRef.current.valueAsNumber = 0;
    }
    inputRef?.current?.stepUp();
    dispatchEvent();
  };
  const onClickDecrement = () => {
    if (inputRef?.current?.value && +inputRef?.current?.value > 0) {
      inputRef?.current?.stepDown();
      dispatchEvent();
    }
  };

  return (
    <Box
      className={productClasses.quantitySelector}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',
        justifyContent: 'space-between',
      }}
    >
      <ButtonBase
        tabIndex={-1}
        disabled={disabled}
        color={disabled ? 'default' : 'primary'}
        className={cx(productClasses.addOrSubtract, productClasses.addBorderRadius)}
        sx={{ color: iconColor }}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClickIncrement}
      >
        <Icon icon={Plus} size="lg" />
      </ButtonBase>
      <TextField
        {...teceoInputConfig}
        {...textFieldProps}
        fullWidth
        className={productClasses.touchTextField}
        variant="standard"
        inputRef={inputRef}
        size="small"
        slotProps={{
          htmlInput: {
            ...textFieldProps.inputProps,
            autoComplete: 'off',
            readOnly: isReadOnly,
          },
        }}
      />
      {showStock && (
        <Typography
          variant="caption"
          component="p"
          color="textSecondary"
          align="right"
          className={productClasses.touchQuantity}
          noWrap
        >
          {stockValue}
        </Typography>
      )}
      <ButtonBase
        tabIndex={-1}
        disabled={disabled}
        color={disabled ? 'default' : 'primary'}
        className={cx(productClasses.addOrSubtract, productClasses.subtractBorderRadius)}
        sx={{ color: iconColor }}
        onClick={onClickDecrement}
        onMouseDown={(event) => event.preventDefault()}
      >
        <Icon icon={Minus} size="lg" />
      </ButtonBase>
    </Box>
  );
};

export default MobileProductInput;
